import { CurrencySchema } from '@pf/contracts';
import { z } from 'zod';
/**
 * The customer session — the website's own identity, not PaymentFlow's.
 *
 * This is the sharpest illustration of why the profile is separate from `@pf/contracts`: PF has
 * no concept of an end user. It knows participants (websites) and operators. The person clicking
 * "deposit" is the *website's* customer, authenticated however the website chooses, and PF only
 * ever sees them as an opaque `account_ref` on a signed server-to-server call.
 *
 * So the profile specifies the session's *shape*, and says nothing about its mechanism. Cookie,
 * bearer token, or an existing SSO — an implementation picks. The reference BFF uses an
 * `HttpOnly` cookie because the reference UI is a browser SPA on the same origin, which is the
 * only combination where CSRF-safe cookies are the simplest correct answer.
 */
/** The signed-in customer, as the BFF knows them. */
export const CustomerSessionSchema = z
    .object({
    /**
     * The website's own identifier for this customer.
     *
     * This is the value the BFF puts in `depositor.account_ref` / `withdrawer.account_ref` when
     * it calls PaymentFlow, and the value PF echoes back in webhooks. It is opaque to PF.
     */
    customer_id: z.string().min(1),
    /** Name to show in the shell. */
    display_name: z.string().min(1),
    /** Contact address. `null` when the website authenticates by something other than email. */
    email: z.string().email().nullable(),
    /** The PaymentFlow participant this website is registered as. Shown for support. */
    participant: z.string().min(1),
    /** Currency this customer transacts in — drives every amount the UI formats. */
    currency: CurrencySchema,
    /** When this session began. */
    signed_in_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * `POST /bff/auth/login` request.
 *
 * Email and password because that is what the reference UI's form collects. A BFF fronting an
 * existing identity provider is free to reject this endpoint entirely (`404`) and have the UI
 * arrive with a session already established — `GET /bff/auth/session` is the only endpoint the
 * profile actually requires for authentication.
 */
export const CustomerLoginRequestSchema = z
    .object({
    email: z.string().email(),
    password: z.string().min(1),
})
    .strict();
/** `POST /bff/auth/logout` response. */
export const CustomerLogoutResponseSchema = z.object({ ok: z.literal(true) }).strict();
//# sourceMappingURL=session.js.map